import { KalturaClient } from "kaltura-typescript-client";
import { UploadTokenAddAction } from "kaltura-typescript-client/api/types/UploadTokenAddAction";
import { UploadTokenUploadAction } from "kaltura-typescript-client/api/types/UploadTokenUploadAction";
import { UploadTokenDeleteAction } from "kaltura-typescript-client/api/types/UploadTokenDeleteAction";
import { KalturaUploadToken } from "kaltura-typescript-client/api/types/KalturaUploadToken";

interface Props {
    serviceUrl: string;
    ks: string;
    onUploadDone?: (uploadTokenId: string, filename: string) => void;
    file: File;
    onProgress?: (loaded: number, total: number) => void;
    onError?: (error: string) => void;
}

export interface UploadCancelHandle {
    cancel: () => void;
}

/**
 * Generic class to handle file upload
 */
class Uploader {
    upload = (props: Props): UploadCancelHandle => {
        const { serviceUrl, ks, file } = props;
        // flag for checking if callbacks should be triggered
        let cancelled = false;
        // id of the token that is used for this upload
        let tokenId: string | null = null;

        let kClient: KalturaClient | null = null;
        // create client
        try {
            kClient = new KalturaClient(
                {
                    endpointUrl: serviceUrl,
                    clientTag: "kms_client",
                },
                {
                    ks: ks,
                }
            );
        }
        catch (e) {
            if (props.onError) {
                props.onError("Cannot connect to Kaltura Client");
            }
            return { cancel: () => {} };
        }

        // create upload token
        const tokenData = new KalturaUploadToken();
        tokenData.fileName = file.name;
        kClient.request(new UploadTokenAddAction({ uploadToken: tokenData })).then(
            (data) => {
                if (cancelled) return;
                if (data) {
                    tokenId = data.id;
                    kClient
                        .request(
                            new UploadTokenUploadAction({
                                fileData: file,
                                uploadTokenId: data.id,
                            }).setProgress((loaded: number, total: number) => {
                                if (cancelled) return;
                                if (props.onProgress) {
                                    props.onProgress(loaded, total);
                                }
                            })
                        )
                        .then(
                            (data) => {
                                if (cancelled) return;
                                if (data && props.onUploadDone) {
                                    props.onUploadDone(data.id, file.name);
                                }
                            },
                            (e: Error) => {
                                if (cancelled) return;
                                if (props.onError) {
                                    props.onError("Failed to upload file: " + e.message);
                                }
                            }
                        );
                }
                else {
                    if (props.onError) {
                        props.onError("Failed to create upload token");
                    }
                }
            },
            (e: Error) => {
                if (cancelled) return;
                if (props.onError) {
                    props.onError("Failed to upload file: " + e.message);
                }
            }
        );

        return {
            cancel: () => {
                cancelled = true;
                if (tokenId) {
                    kClient.request(new UploadTokenDeleteAction({ uploadTokenId: tokenId })).catch(
                        (e: Error) => {
                            // Best-effort cleanup; log failure to avoid unhandled promise rejection
                            // and to aid in debugging potential orphaned upload tokens.
                            // eslint-disable-next-line no-console
                            console.error("Failed to delete upload token:", e);
                        }
                    );
                }
            },
        };
    };
}

export default Uploader;
